import { isValidCenter } from './isValidCenter';
import { haversineMi } from './haversine';
import { safetyFromRiskScore, affordabilityFromRent } from './scoring';

export function stateZctas(state, zctaCrimeData) {
  const want = String(state || '').trim().toUpperCase();
  if (!want || !zctaCrimeData || typeof zctaCrimeData !== 'object') return [];
  return Object.entries(zctaCrimeData)
    .filter(([, entry]) => String(entry?.state || '').trim().toUpperCase() === want)
    .map(([zcta]) => zcta);
}

/** Miles radius around work/home for map coverage area (score averaging). */
export const ZCTA_COVERAGE_RADIUS_MI = 20;

/**
 * ZCTAs within radius of work and/or home (the full map coverage area).
 * Uses crime data centroids for distance check.
 */
export function coverageAreaZctas(workCenter, homeCenter, zctaCrimeData) {
  if (!zctaCrimeData || typeof zctaCrimeData !== 'object') return [];
  const centers = [];
  if (isValidCenter(workCenter)) centers.push(workCenter);
  if (isValidCenter(homeCenter)) centers.push(homeCenter);
  if (!centers.length) return [];
  const zctas = new Set();
  for (const [zcta, entry] of Object.entries(zctaCrimeData)) {
    if (entry == null || entry.risk_score == null || entry.risk_score < 0) continue;
    const lat = entry?.lat;
    const lon = entry?.lon;
    if (lat == null || lon == null) continue;
    const inRange = centers.some(([cLat, cLng]) =>
      haversineMi(cLat, cLng, lat, lon) <= ZCTA_COVERAGE_RADIUS_MI
    );
    if (inRange) zctas.add(zcta);
  }
  return Array.from(zctas);
}

/**
 * Average Safety, Cost, and Livability scores across the full map coverage area.
 * Returns { safety, cost, livability, livabilityBreakdown } (1–10) or nulls where no data.
 */
export function averageScoresForCoverageArea(zctas, zctaCrimeData, zctaMedianRent, zctaLivabilityData) {
  if (!zctas.length) return { safety: null, cost: null, livability: null, livabilityBreakdown: null };
  const safetyScores = [];
  const rents = [];
  const livScores = [];
  for (const zcta of zctas) {
    const crime = zctaCrimeData?.[zcta];
    if (crime?.risk_score != null && crime.risk_score >= 0) {
      const s = safetyFromRiskScore(crime.risk_score);
      if (s != null) safetyScores.push(s);
    }
    const rent = zctaMedianRent?.[zcta];
    if (rent != null && rent > 0) rents.push(Number(rent));
    const liv = zctaLivabilityData?.[zcta];
    if (liv && (liv.livability != null || (liv.education != null && liv.ba_plus_pct > 0) || (liv.income != null && liv.median_income > 0) || (liv.amenity != null && liv.amenity_count > 0))) {
      const scores = [];
      if (liv.education != null && liv.ba_plus_pct > 0) scores.push(liv.education);
      if (liv.income != null && liv.median_income > 0) scores.push(liv.income);
      if (liv.amenity != null && liv.amenity_count > 0) scores.push(liv.amenity);
      const eff = scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10 : liv.livability;
      if (eff != null) livScores.push(eff);
    }
  }
  const avgSafety = safetyScores.length ? Math.round(safetyScores.reduce((a, b) => a + b, 0) / safetyScores.length) : null;
  const avgRent = rents.length ? rents.reduce((a, b) => a + b, 0) / rents.length : null;
  const avgCost = avgRent != null ? affordabilityFromRent(avgRent) : null;
  const avgLiv = livScores.length ? Math.round((livScores.reduce((a, b) => a + b, 0) / livScores.length) * 10) / 10 : null;

  // Average livability breakdown for tooltip
  let livabilityBreakdown = null;
  const livEntries = [];
  for (const zcta of zctas) {
    const liv = zctaLivabilityData?.[zcta];
    if (liv && ((liv.education != null && liv.ba_plus_pct > 0) || (liv.income != null && liv.median_income > 0) || (liv.amenity != null && liv.amenity_count > 0))) {
      livEntries.push(liv);
    }
  }
  if (livEntries.length > 0) {
    const avg = (key, filter) => {
      const withVal = livEntries.filter((e) => filter(e));
      if (!withVal.length) return null;
      const sum = withVal.reduce((a, e) => a + (e[key] ?? 0), 0);
      return key === 'median_income' || key === 'amenity_count'
        ? Math.round(sum / withVal.length)
        : Math.round((sum / withVal.length) * 10) / 10;
    };
    livabilityBreakdown = {
      education: avg('education', (e) => e.education != null && e.ba_plus_pct > 0),
      income: avg('income', (e) => e.income != null && e.median_income > 0),
      amenity: avg('amenity', (e) => e.amenity != null && e.amenity_count > 0),
      ba_plus_pct: avg('ba_plus_pct', (e) => e.ba_plus_pct > 0),
      median_income: avg('median_income', (e) => e.median_income > 0),
      amenity_count: avg('amenity_count', (e) => e.amenity_count > 0),
    };
  }
  return { safety: avgSafety, cost: avgCost, livability: avgLiv, livabilityBreakdown };
}
